import {
  TextInput,
  Button,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { IconZoomExclamation } from '@tabler/icons-react';
import validate from '@/helpers/validations';
import Loading from '@/components/Loading';
import { useGetTranchesDataMutation, useUpdateTranchesDataMutation } from '@/services/api';
import { getMutationResult } from '@/helpers/query';
import { Page } from '@/components';
import classes from './style.module.css';

const TYPE_LABELS = {
  mortgage: 'משכנתא',
  general: 'כל מטרה',
};

const Rate = () => {
  const { t } = useTranslation();
  const [ratesLoading, setRatesLoading] = useState(false);
  const [tranchesDataMutationConfig] = useGetTranchesDataMutation();
  const [updateTranchesDataMutationConfig] = useUpdateTranchesDataMutation();
  const [loading, { open, close }] = useDisclosure(false);
  const [isError, setIsError] = useState(false);
  const fieldValidations = {
    rate: (value, isDisabled) => {
      if (isDisabled) {
        return null; // Skip validation if the input is disabled
      }
      return validate(value, {
        required: t('globals.validations.required'),
        decimalNumber: t('globals.validations.number'),
      });
    },
  };

  const form = useForm({
    initialValues: {
      tranches: [],
      mixes: [],
    },
    validateInputOnBlur: true,
    validate: {
      tranches: {
        mortgageRateCreditPro: fieldValidations.rate,
        mortgageRateBank: fieldValidations.rate,
        generalRateCreditPro: fieldValidations.rate,
        generalRateBank: fieldValidations.rate,
        linkage: fieldValidations.rate,
      },
    },
  });

  const fetchData = async () => {
    setRatesLoading(true);
    try {
      const {
        data,
        isError: tranchesDataIsError,
      } = await getMutationResult({}, tranchesDataMutationConfig);
      if (tranchesDataIsError) {
        setIsError(true);
        setRatesLoading(false);
        return;
      }
      const { tranches } = data;

      // Map the response data to the form structure
      const tranchesData = tranches.map((tranche) => ({
        id: tranche._id, // Use `_id` from the response
        hebrew: tranche.hebrew, // Hebrew name of the tranche
        rates: tranche.rates.map((rate) => ({
          creditPro: rate.creditPro, // Whether it's CreditPro or not
          rate: rate.rate, // The rate value
          type: rate.type, // The type (e.g., "mortgage" or "general")
          isCycle: rate.isCycle,
        })),
        linkage: tranche.linkage || '', // Linkage value
        linkageShow: tranche.linkageShow !== undefined
          ? tranche.linkageShow : true, // Whether linkage is shown
      }));

      // Set the mapped data to the form
      form.setFieldValue('tranches', tranchesData);
    } catch (error) {
      // console.error('Error fetching data:', error);
    }
    setRatesLoading(false);
  };
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    if (form.validate().hasErrors) {
      return;
    }

    try {
      const formData = form.values.tranches.map((tranche) => ({
        id: tranche.id,
        rates: tranche.rates.map((rate) => ({
          creditPro: rate.creditPro,
          rate: rate.rate,
          type: rate.type,
          isCycle: rate.isCycle,
        })),
        linkage: tranche.linkage || 0,
      }));

      const {
        data,
        isError: updateTranchesDataIsError,
      } = await getMutationResult({ tranches: formData }, updateTranchesDataMutationConfig);

      // eslint-disable-next-line no-unused-vars
      const response = data;

      if (updateTranchesDataIsError) {
        open();
        notifications.show({
          title: 'שגיאה בשמירת הנתונים',
          message: 'הנתונים לא נשמרו בהצלחה',
          color: 'red',
        });
        return;
      }
      open();

      notifications.show({
        title: 'המידע נשמר בהצלחה',
        message: 'המידע נשמר בהצלחה',
      });
    } catch (error) {
      notifications.show({
        title: 'המידע לא נשמר בהצלחה',
        message: 'המידע לא נשמר בהצלחה',
        color: 'red',
      });
    }
    close();
  };

  if (ratesLoading) return (<Loading />);
  if (isError) {
    return (
      <div className={classes.errorContainer}>
        <h2>שגיאה בטעינת הנתונים</h2>
        <IconZoomExclamation size={16} />
      </div>
    );
  }

  return (
    <>
      <Page.Head
        title={t('pages.settings.pages.rate.title')}
        subtitle={t('pages.settings.pages.rate.description')}
      />
      <div className={classes.container}>
        <div className={classes.tabContent}>
          <div className={classes.tranchesWrapper}>
            {form.values.tranches.map((item, idx) => (
              <div className={classes.trackBox} key={idx}>
                <h3>{item.hebrew}</h3>

                <div className={classes.rates}>
                  {item.rates.map((rate, rateIdx) => (
                    <div key={'rateIdx'}>
                      <TextInput
                    key={rateIdx}
                    label={`${TYPE_LABELS[rate.type] || rate.type} - ${rate.creditPro ? 'CreditPro' : 'Bank'} ${rate.isCycle ? '(מחזור)' : ''}`}
                    placeholder="שיעור ריבית"
                    rightSection={'%'}
                    value={rate.rate}
                    onChange={(e) => {
                      const updatedRates = [...item.rates];
                      updatedRates[rateIdx].rate = e.target.value;
                      form.setFieldValue(`tranches.${idx}.rates`, updatedRates);
                    }}
                    error={fieldValidations.rate(rate.rate)}
                    required
                      />
                    </div>
                  ))}
                </div>

                <TextInput
                  rightSection={'%'}
                  label="הצמדה"
                  placeholder="שיעור הצמדה"
                  classNames={{ input: classes.input }}
                  disabled={!item.linkageShow}
                  value={item.linkage || ''}
                  onChange={(e) => form.setFieldValue(`tranches.${idx}.linkage`, e.target.value)}
                  error={fieldValidations.rate(item.linkage, !item.linkageShow)}
                />
              </div>
            ))}
          </div>
        </div>
        <div className={classes.buttonsWrapper}>
          <Button onClick={handleSubmit} loading={loading} className={classes.submitButton}>
            שמירה
          </Button>
        </div>
      </div>
    </ >
  );
};

export default Rate;
